// src/components/Permission/DeletePermissionModal.tsx
import React from 'react';
import { Trash2 } from 'lucide-react';
import { deleteRoutePermission } from '../../services/permissionService';

interface DeletePermissionModalProps {
    isOpen: boolean;
    onClose: () => void;
    permission: { id: number; path: string } | null;
    onPermissionDeleted: () => void;
}

const DeletePermissionModal: React.FC<DeletePermissionModalProps> = ({
    isOpen,
    onClose,
    permission,
    onPermissionDeleted
}) => {
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleDelete = async () => {
        if (!permission) return;
        
        setIsDeleting(true);
        setError(null);

        try {
            await deleteRoutePermission(permission.id);
            onPermissionDeleted();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Error al eliminar el permiso');
            console.error("Error al eliminar:", err);
        } finally {
            setIsDeleting(false);
        }
    };

    if (!isOpen || !permission) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white p-8 rounded-2xl w-full max-w-md border border-gray-100 shadow-2xl">
                <div className="flex justify-between items-start mb-6">
                    <h3 className="text-[20px] font-bold text-gray-900">Confirmar eliminación</h3>
                    <button 
                        onClick={onClose} 
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <p className="mb-8 text-gray-600">
                    ¿Estás seguro que deseas eliminar el permiso para la ruta{' '}
                    <strong className="text-red-600">"{permission.path}"</strong>?
                </p>

                {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

                <div className="flex justify-end gap-3 mt-8">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200 font-medium text-[14px]"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium shadow-sm flex items-center text-[14px]"
                    >
                        <Trash2 className="w-4 h-4 mr-2" />
                        {isDeleting ? 'Eliminando...' : 'Eliminar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DeletePermissionModal;